/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { VNode } from "preact";
import { TalerMerchantApi } from "@gnu-taler/taler-util";
import { currentBackendUrl, currentAccount } from "../stores/session.js";
import { SignInScreen, type SignInScreenProps } from "./SignInScreen.js";
import { useTranslation } from "../context/translation.js";
import type { ApiFailure } from "../api/failure.js";
import { ApiErrorBanner } from "../ui/ApiErrorBanner.js";

export interface MfaChallengeScreenProps {
  token?: string;
  accountLabel?: string;
  challengeAccount?: string;
  actionNotice?: string;
  challengeResponse?: TalerMerchantApi.ChallengeResponse;
  solvedChallengeIds?: string[];
  onSuccess?: (solvedChallengeIds: string[]) => Promise<void>;
  onCancel?: () => void;
  /** Reaching the server is the route's job, exactly as on the sign-in screen. */
  onSendChallenge?: SignInScreenProps["onSendChallenge"];
  onSolveChallenge?: SignInScreenProps["onSolveChallenge"];
  completionError?: ApiFailure;
}

export function MfaChallengeScreen(props: MfaChallengeScreenProps): VNode {
  const { t } = useTranslation();
  if (props.completionError) {
    return (
      <div class="max-w-xl mx-auto p-6 space-y-4">
        <ApiErrorBanner
          error={props.completionError}
          title={t`Your code was accepted, but the action did not finish`}
        />
        <p class="text-sm text-gray-600">
          {t`The result may be uncertain. Return to the previous screen and refresh before trying again.`}
        </p>
        <button
          type="button"
          onClick={props.onCancel}
          class="px-4 py-2 rounded-md bg-taler-brand text-white text-sm font-bold"
        >
          {t`Return`}
        </button>
      </div>
    );
  }
  return (
    <SignInScreen
      initialBackendUrl={currentBackendUrl.value}
      initialAccount={props.challengeAccount || currentAccount.value}
      initialChallengeResponse={props.challengeResponse}
      initialSolvedChallengeIds={props.solvedChallengeIds}
      // The rest of the portal asks for "the code we sent"; this was the one
      // screen that said MFA and Multi-Factor Authentication to a shopkeeper.
      title={t`Confirm it is you`}
      subtitle={props.accountLabel
        ? t`Before this goes ahead, enter the six-digit code sent to you for ${props.accountLabel}.`
        : t`Before this goes ahead, enter the six-digit code sent to you for your merchant account.`}
      submitButtonText={t`Confirm`}
      accountLabel={props.accountLabel}
      actionNotice={props.actionNotice || (props.accountLabel
        ? t`Deleting bank account ${props.accountLabel}`
        : t`Deleting a bank account`)}
      onMfaSuccess={props.onSuccess}
      onCancel={props.onCancel}
      onSendChallenge={props.onSendChallenge}
      onSolveChallenge={props.onSolveChallenge}
    />
  );
}
